import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "contexts/AuthContext";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { Modal } from "components/common/Modal";
import { Button } from "components/common/Button";
import { SmartSurveyorUploadWizard } from "../../SmartSurveyorForRudolfFritz/SmartSurveyorUploadWizard";
import { useCreditPreflightCheck } from "../../hooks/useCreditPreflightCheck";
import { useFileCategorySelection } from "../../hooks/useFileCategorySelection";
import { FileCategoryCardsGrid } from "../../shared/FileCategoryCardsGrid";
import type { AdditionalUploadModalProps } from "./types";

export const AdditionalUploadModal = ({
  isOpen,
  onClose,
  projectId,
  onUploadComplete,
}: AdditionalUploadModalProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [showWizard, setShowWizard] = useState(false);

  const {
    selectedFiles,
    setters,
    hasRequiredFiles,
    pdfFileCount,
    handleRemoveFile,
    resetFileState,
  } = useFileCategorySelection();

  const {
    isChecking: isCheckingCredits,
    error: creditError,
    runCheck: runCreditPreflightCheck,
  } = useCreditPreflightCheck({
    pdfFileCount,
    isGlobalAdmin: user?.isGlobalAdmin,
    uploadKind: "additional",
  });

  const handleAnalyzeClick = useCallback(async () => {
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_FILES_SUBMITTED, {
      uploadKind: "additional",
      floorPlanCount: selectedFiles.floorPlans.length,
      diagramCount: selectedFiles.electricalDiagrams.length,
      boqCount: selectedFiles.billOfQuantities.length,
      otherCount: selectedFiles.otherFiles.length,
    });

    const canProceed = await runCreditPreflightCheck();
    if (canProceed) {
      setShowWizard(true);
    }
  }, [selectedFiles, runCreditPreflightCheck]);

  const handleWizardComplete = useCallback(() => {
    setShowWizard(false);
    resetFileState();
    onUploadComplete();
  }, [onUploadComplete, resetFileState]);

  const handleClose = useCallback(() => {
    if (showWizard) return;
    resetFileState();
    onClose();
  }, [onClose, resetFileState, showWizard]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="xl"
        disableBackdropClose={showWizard}
        disableEscapeClose={showWizard}
      >
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t("smartSurveyor.additionalUpload.title")}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {t("smartSurveyor.additionalUpload.subtitle")}
            </p>
          </div>
          {creditError && (
            <div className="w-full p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 font-medium">{creditError}</p>
            </div>
          )}
          <FileCategoryCardsGrid
            selectedFiles={selectedFiles}
            setters={setters}
          />
          <div className="flex justify-end gap-3">
            <Button onClick={handleClose} disabled={showWizard}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleAnalyzeClick}
              disabled={!hasRequiredFiles || isCheckingCredits}
            >
              {isCheckingCredits
                ? t("smartSurveyor.credits.checking", "Checking credits...")
                : t("smartSurveyor.analysisWorkflow.analyzeButton")}
            </Button>
          </div>
        </div>
      </Modal>
      {showWizard && (
        <SmartSurveyorUploadWizard
          projectId={projectId}
          uploadKind="additional"
          selectedFiles={selectedFiles}
          onClose={() => setShowWizard(false)}
          onUploadComplete={handleWizardComplete}
          onRemoveFile={handleRemoveFile}
        />
      )}
    </>
  );
};
